/**
 * 46. Purchase Requests — intake list; budget-holder approvals; convert
 * to PO.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the request.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatMoney, formatDate } from "@/lib/money";

interface PurchaseRequestRow {
  id: string;
  requestNumber: string;
  requestedBy: string;
  department: string;
  requestedDate: string;
  expectedDate: string | null;
  total: string;
  currencyCode: string;
  status:
    "Draft" | "Submitted" | "Approved" | "Rejected" | "Converted" | "Cancelled";
}

export function PurchaseRequestsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<PurchaseRequestRow>(
    ["procurement", "requests"],
    "/procurement/purchase-requests",
  );

  return (
    <div>
      <PageHeader
        title={t("procurement.requests.title")}
        subtitle={t("procurement.requests.subtitle")}
        actions={
          <PendingButton variant="primary" reason="notScheduled" slice="B1">
            {t("procurement.requests.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("procurement.requests.list"), data.total)}>
          <DataTable<PurchaseRequestRow>
            rows={data.items}
            columns={[
              {
                key: "requestNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.requestNumber}</span>
                ),
              },
              { key: "requestedBy", label: t("fields.requestedBy") },
              {
                key: "department",
                label: t("procurement.requests.department"),
              },
              {
                key: "requestedDate",
                label: t("fields.requested"),
                render: (r) => formatDate(r.requestedDate),
              },
              {
                key: "expectedDate",
                label: t("procurement.requests.expected"),
                render: (r) =>
                  r.expectedDate ? formatDate(r.expectedDate) : "—",
              },
              {
                key: "total",
                label: t("fields.total"),
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Approved" || r.status === "Converted" ? (
                    <StatusBadge status={r.status} type="PurchaseRequest" />
                  ) : (
                    <Badge
                      tone={
                        r.status === "Rejected" || r.status === "Cancelled"
                          ? "danger"
                          : r.status === "Submitted"
                            ? "warning"
                            : "default"
                      }
                    >
                      {statusLabel(t, "PurchaseRequest", r.status)}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
